import { Injectable } from '@angular/core';
import { Observable, map, forkJoin, of, from, throwError, tap } from 'rxjs';
import { catchError, mergeMap, reduce } from 'rxjs/operators';
import { HTTPService } from 'src/app/services/http.service';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import {
  BatchProgress,
  ConciliadoraConfig,
  ConciliadoraTipoConfig,
  ConciliadoraResumoTipo,
  ConciliadoraCabecalho,
  ConciliadoraRegistro,
  DashboardStats,
  ExecutarOpts,
  FlowExecLogFinishRequest,
  FlowExecLogGetResponse,
  FlowExecLogListFilter,
  FlowExecLogListResponse,
  FlowExecLogStartRequest,
  FlowExecLogStartResponse,
  FlowJobStart,
  FlowJobStatus,
  MapeamentoColuna,
  MktpCfgOption,
  COLUNAS_REGISTRO,
} from '../models/conciliadora.models';

@Injectable({ providedIn: 'root' })
export class ConciliadoraService {
  constructor(
    private http: HTTPService,
    private uas: UserAuthenticationServices
  ) {}

  private _selectedCompany: { codecompany: any; codecontract: any } | null = null;

  get companyList(): any[] {
    return this.uas.user?.companylist || [];
  }

  get hasMultipleCompanies(): boolean {
    return this.companyList.length > 1;
  }

  setSelectedCompany(codecompany: any, codecontract: any): void {
    this._selectedCompany = { codecompany, codecontract };
  }

  get selectedCompanyCode(): any {
    return this._selectedCompany?.codecompany ?? null;
  }

  private companyFilter(alias?: string): string {
    const col = alias ? `${alias}.CODECOMPANY` : 'CODECOMPANY';
    if (this._selectedCompany) {
      return `${col} = ${this._selectedCompany.codecompany}`;
    }
    if (this.uas.user?.profile === 'ADMIN') return '0=0';
    return `${col} IN (${this.uas.getCompanies()})`;
  }

  private sqlEscape(v: string): string {
    return String(v ?? '').replace(/'/g, "''");
  }

  private mapSqlRows<T>() {
    return map((res: any) => (res['SQL'] as T[]) || []);
  }

  private get codecompany(): any {
    if (this._selectedCompany) return this._selectedCompany.codecompany;
    return this.uas.user?.companylist?.[0]?.codecompany;
  }

  private get codecontract(): any {
    if (this._selectedCompany) return this._selectedCompany.codecontract;
    return this.uas.user?.companylist?.[0]?.codecontract;
  }

  private get username(): string {
    return this.uas.user?.username || '';
  }

  private query(sql: string, entityName: string): Observable<any> {
    return this.http.post({ entityName, sql }, 'JsonBrokerServices/findnative', false, true, true);
  }

  private save(entityName: string, data: Record<string, any>): Observable<any> {
    const ob = { entityName, ...data };
    return this.http.post(ob, 'JsonBrokerServices/save', true, true, true);
  }

  private remove(entityName: string, condition: string): Observable<any> {
    return this.http.post({ entityName, condition }, 'JsonBrokerServices/remove', false, true, true);
  }

  // ========== CONFIGURACOES ==========

  getConfigs(): Observable<ConciliadoraConfig[]> {
    return this.query(
      `SELECT * FROM MKTPCONFCONCILIADORA WHERE ${this.companyFilter()} ORDER BY NUCONF DESC`,
      'MKTPCONFCONCILIADORA'
    ).pipe(this.mapSqlRows<ConciliadoraConfig>());
  }

  testarSql(sql: string): Observable<any> {
    return this.query(this.resolvePreviewParams(sql), 'MKTPREGISTROSCONCILIADORA');
  }

  /** Colunas REAIS da tabela de registros (INFORMATION_SCHEMA), na ordem fisica.
   *  Base do grid dinamico (item 2): coluna nova na tabela aparece aqui sozinha.
   *  Nao filtra tenant (e metadado de schema). */
  getColunasTabela(): Observable<string[]> {
    return this.query(
      `SELECT COLUMN_NAME FROM INFORMATION_SCHEMA.COLUMNS
       WHERE TABLE_NAME = 'MKTPREGISTROSCONCILIADORA'
       ORDER BY ORDINAL_POSITION`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(
      map((res: any) => ((res['SQL'] as any[]) || []).map((r) => r.COLUMN_NAME as string)),
      tap((cols) => { this.tableColumns = new Set(cols.map((c) => (c || '').toUpperCase())); }),
    );
  }

  /**
   * O "Testar SQL" envia o SQLBUSCA cru pro findnative, que NAO substitui @params
   * (a substituicao so acontece em runtime, no engine do flow). Logo, qualquer
   * @PARAM cru (ex.: @CODECOMPANY) quebra o SQL Server ("Must declare the scalar
   * variable @X") e estoura o dialog global de erro. Para o PREVIEW resolvemos os
   * params aqui, mantendo a sintaxe valida:
   *  - @CODECOMPANY -> empresa(s) do escopo (selecionada, ou as do usuario);
   *  - demais @PARAM de runtime -> NULL (o preview so inspeciona colunas/amostra).
   */
  private resolvePreviewParams(sql: string): string {
    if (!sql) return sql;
    const companies =
      this.selectedCompanyCode != null
        ? String(this.selectedCompanyCode)
        : this.uas.getCompanies() || '0';
    return sql
      .replace(/@CODECOMPANY\b/gi, companies)
      .replace(/@[A-Za-z_]\w*/g, 'NULL');
  }

  saveConfig(config: Partial<ConciliadoraConfig>): Observable<any> {
    const ob: any = {
      DESCRICAO: config.DESCRICAO,
      VALIDA: config.VALIDA || 'N',
      ATIVO: config.ATIVO || 'S',
      SQLBUSCA: config.SQLBUSCA || null,
      MAPEAMENTO: config.MAPEAMENTO || null,
      LABELSGRADE: config.LABELSGRADE || null,
      COLUNASGRADE: config.COLUNASGRADE ?? null,
      IDCONFLOCALIZANOTA: config.IDCONFLOCALIZANOTA ?? null,
    };
    if (config.NUCONF) {
      ob.NUCONF = config.NUCONF;
      ob.MODIFIEDBY = this.username;
      ob.MODIFIEDON = new Date();
    } else {
      ob.CODECOMPANY = this.codecompany;
      ob.CODECONTRACT = this.codecontract;
      ob.CREATEDBY = this.username;
      ob.CREATEDON = new Date();
    }
    return this.save('MKTPCONFCONCILIADORA', ob);
  }

  deleteConfig(nuconf: number): Observable<any> {
    return forkJoin([
      this.remove('MKTPCONFTIPOSCONC', `NUCONF = ${nuconf}`),
      this.remove('MKTPCONFCONCILIADORA', `NUCONF = ${nuconf} AND ${this.companyFilter()}`),
    ]);
  }

  // ========== TIPOS POR CONFIGURACAO ==========

  getTiposByConfig(nuconf: number): Observable<ConciliadoraTipoConfig[]> {
    return this.query(
      // ORDER BY IDTIPO (nao por ORDEM) de proposito: mantem a LEITURA resiliente
      // caso a coluna ORDEM ainda nao exista no ambiente (migracao 12 pendente).
      // A ordem de EXECUCAO usa o sort client-side em executeProcessamento.
      `SELECT * FROM MKTPCONFTIPOSCONC WHERE NUCONF = ${nuconf} AND ${this.companyFilter()} ORDER BY IDTIPO`,
      'MKTPCONFTIPOSCONC'
    ).pipe(this.mapSqlRows<ConciliadoraTipoConfig>());
  }

  saveTipo(tipo: Partial<ConciliadoraTipoConfig>): Observable<any> {
    const ob: any = {
      NUCONF: tipo.NUCONF,
      TIPOS: tipo.TIPOS,
      DESCRICAO: tipo.DESCRICAO || null,
      IDCONF: tipo.IDCONF || null,
      PARAMETROS: tipo.PARAMETROS || null,
      ORDEM: tipo.ORDEM ?? null,
    };
    if (tipo.IDTIPO) {
      ob.IDTIPO = tipo.IDTIPO;
      ob.MODIFIEDON = new Date();
    } else {
      ob.CODECOMPANY = this.codecompany;
      ob.CODECONTRACT = this.codecontract;
      ob.CREATEDON = new Date();
    }
    return this.save('MKTPCONFTIPOSCONC', ob);
  }

  deleteTipo(idtipo: number): Observable<any> {
    return this.remove('MKTPCONFTIPOSCONC', `IDTIPO = ${idtipo} AND ${this.companyFilter()}`);
  }

  // ========== CABECALHOS ==========

  getCabecalhos(nuconf?: number): Observable<ConciliadoraCabecalho[]> {
    let where = this.companyFilter('c');
    if (nuconf) where += ` AND c.NUCONF = ${nuconf}`;
    return this.query(
      `SELECT c.*, cf.DESCRICAO AS CONFIG_DESCRICAO
       FROM MKTPCABCONCILIADORA c
       LEFT JOIN MKTPCONFCONCILIADORA cf ON cf.NUCONF = c.NUCONF
       WHERE ${where} ORDER BY c.CODIGO DESC`,
      'MKTPCABCONCILIADORA'
    ).pipe(this.mapSqlRows<ConciliadoraCabecalho>());
  }

  saveCabecalho(cab: Partial<ConciliadoraCabecalho>): Observable<any> {
    const ob: any = {};
    const codigoNum = cab.CODIGO != null ? Number(cab.CODIGO) : null;
    if (codigoNum && !isNaN(codigoNum)) {
      ob.CODIGO = codigoNum;
      if (cab.NUCONF != null) ob.NUCONF = cab.NUCONF;
      if (cab.NROCONCILIACAO !== undefined) ob.NROCONCILIACAO = cab.NROCONCILIACAO;
      if (cab.DTBAIXA !== undefined) ob.DTBAIXA = cab.DTBAIXA;
      if (cab.OBSERVACAO !== undefined) ob.OBSERVACAO = cab.OBSERVACAO;
      if (cab.AGENDARPROCESSAMENTO !== undefined) ob.AGENDARPROCESSAMENTO = cab.AGENDARPROCESSAMENTO;
      if (cab.STATUS !== undefined) ob.STATUS = cab.STATUS;
      ob.MODIFIEDBY = this.username;
      ob.MODIFIEDON = new Date();
    } else {
      ob.NUCONF = cab.NUCONF;
      ob.NROCONCILIACAO = cab.NROCONCILIACAO || null;
      ob.CODECOMPANY = this.codecompany;
      ob.CODECONTRACT = this.codecontract;
      ob.DTCRIACAO = new Date();
      ob.DTBAIXA = cab.DTBAIXA || null;
      ob.AGENDARPROCESSAMENTO = cab.AGENDARPROCESSAMENTO || 'N';
      ob.STATUS = 'Aberto';
      ob.OBSERVACAO = cab.OBSERVACAO || null;
      ob.CREATEDBY = this.username;
    }
    return this.save('MKTPCABCONCILIADORA', ob);
  }

  deleteCabecalho(codigo: number): Observable<any> {
    return forkJoin([
      this.remove('MKTPREGISTROSCONCILIADORA', `CODIGO = ${codigo}`),
      this.remove('MKTPCABCONCILIADORA', `CODIGO = ${codigo} AND ${this.companyFilter()}`),
    ]);
  }

  // ========== REGISTROS ==========

  /** Colunas do grid que podem ser ordenadas (whitelist anti-injection do ORDER BY). */
  // Colunas ordenaveis (anti-injection): cobre TODAS as colunas reais da
  // MKTPREGISTROSCONCILIADORA, pra que as colunas extras do grid dinamico (item 2)
  // tambem possam ordenar. Colunas fora daqui caem em SEQUENCIA.
  private static readonly SORT_WHITELIST = new Set<string>([
    'SEQUENCIA', 'DATADOC', 'DATAREC', 'VLRTOT', 'VLRLIQ', 'VLRTAXA', 'VLRTAXAANT', 'VLRPREV',
    'TIPO', 'NSU', 'TID', 'NUAUT', 'PEDIDO', 'NOTA', 'ESTABELECIMENTO',
    'NUCARTAO', 'MODALIDADE', 'BANDEIRA', 'QTDPARCELA', 'PARCELA',
    'STATUS', 'NUNOTA', 'NUFIN', 'NUFINNOVO', 'LIBERADO', 'NSII', 'STATUSCON',
    'CODIGO', 'NRO_CONCILIACAO', 'ID_KONCILI', 'MARKETPLACE', 'ID',
  ]);

  /** Cache (nivel de tabela) das colunas REAIS de MKTPREGISTROSCONCILIADORA,
   * preenchido por getColunasTabela(). Quando disponivel, o ORDER BY dinamico
   * so aceita coluna que existe de fato (evita 500 por coluna inexistente);
   * enquanto null (nao carregado), mantem o fallback do safeId. */
  private tableColumns: Set<string> | null = null;

  /** Cache: a coluna STATUSCON existe nesta base? (sonda 1x). Enquanto a rotina
   * nao entrou em prod, bases antigas (13.65/local) podem nao ter a coluna. */
  private statusConAvailable: boolean | null = null;

  private ensureStatusCap(): Observable<boolean> {
    if (this.statusConAvailable !== null) return of(this.statusConAvailable);
    return this.query(
      `SELECT CASE WHEN COL_LENGTH('MKTPREGISTROSCONCILIADORA','STATUSCON') IS NULL THEN 0 ELSE 1 END AS h`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(
      map((res: any) => {
        this.statusConAvailable = (Number(res?.['SQL']?.[0]?.h) || 0) === 1;
        return this.statusConAvailable;
      }),
      catchError(() => { this.statusConAvailable = false; return of(false); })
    );
  }

  /**
   * CASE do STATUS canonico (C/P/D/V/E/X). Se STATUSCON existir na base (gravado
   * pelo fluxo), prefere-o; senao deriva so do LIBERADO (texto livre) — assim NAO
   * quebra em base que ainda nao tem a coluna. Centralizado: resumo, cards, filtro
   * e coluna do grid usam identico.
   */
  private statusExpr(): string {
    const fb = `WHEN LIBERADO IS NULL THEN 'P'
        WHEN LIBERADO LIKE '%divergente%' THEN 'D'
        WHEN LIBERADO LIKE '%localizad%' THEN 'X'
        WHEN LIBERADO LIKE 'Erro%' THEN 'E'
        ELSE 'C'`;
    return this.statusConAvailable
      ? `CASE WHEN STATUSCON IN ('C','D','V','E','X') THEN STATUSCON ${fb} END`
      : `CASE ${fb} END`;
  }

  getRegistros(
    codigo: number,
    filtroTipo?: string,
    filtroStatus?: string,
    filtroLiberado?: string,
    offset = 0,
    limit = 200,
    orderBy?: string,
    orderDir?: 'ascend' | 'descend' | null
  ): Observable<ConciliadoraRegistro[]> {
    return this.ensureStatusCap().pipe(mergeMap(() => {
    let where = `CODIGO = ${codigo} AND ${this.companyFilter()}`;
    if (filtroTipo) where += ` AND TIPO = '${this.sqlEscape(filtroTipo)}'`;
    if (filtroStatus) where += ` AND STATUS = '${this.sqlEscape(filtroStatus)}'`;
    // filtroLiberado pode vir como CODIGO canonico (C/P/D/V/E/X) OU como o rotulo
    // legado ('Liberado'/'Pendente'/'Valor Divergente'/'Erro'). Mapeia pro codigo
    // e filtra pelo status EFETIVO (compat: nao quebra os botoes de filtro atuais).
    if (filtroLiberado) {
      const legacy: Record<string, string> = {
        'Liberado': 'C', 'Pendente': 'P', 'Valor Divergente': 'D', 'Erro': 'E',
        'Nao localizado': 'X', 'Não localizado': 'X', 'Provisao': 'V', 'Provisão': 'V',
      };
      const code = ['C', 'P', 'D', 'V', 'E', 'X'].includes(filtroLiberado)
        ? filtroLiberado
        : legacy[filtroLiberado];
      if (code) {
        where += ` AND (${this.statusExpr()}) = '${code}'`;
      }
    }
    // ORDER BY dinamico com whitelist (anti-injection). SEQUENCIA como desempate
    // para paginacao estavel quando a coluna ordenada tem valores repetidos.
    // orderBy vem de uma coluna REAL do grid (curada ou extra do INFORMATION_SCHEMA).
    // Aceita se estiver na whitelist OU for um identificador SQL seguro (letras/
    // digitos/_, ate 40 chars) — cobre colunas novas adicionadas por DDL sem abrir
    // injecao (o regex barra aspas/espacos/;). Fora disso, cai em SEQUENCIA.
    // safeId = gate ANTI-INJECAO (sempre): barra aspas/espacos/;. Alem disso, quando
    // ja conhecemos as colunas reais da tabela (tableColumns carregado), exige que a
    // coluna exista de fato — evita 500 por coluna inexistente. Enquanto nao carregou,
    // mantem o comportamento anterior (whitelist estatica). Nunca rejeita coluna valida.
    const safeId = /^[A-Za-z_][A-Za-z0-9_]{0,39}$/;
    const known = this.tableColumns;
    const colOk = !!orderBy && safeId.test(orderBy) && (
      ConciliadoraService.SORT_WHITELIST.has(orderBy) ||
      (known ? known.has(orderBy.toUpperCase()) : true)
    );
    const col = colOk ? (orderBy as string) : 'SEQUENCIA';
    const dir = orderDir === 'descend' ? 'DESC' : 'ASC';
    const orderClause = col === 'SEQUENCIA'
      ? `SEQUENCIA ${dir}`
      : `${col} ${dir}, SEQUENCIA ASC`;

    // STATUSEFETIVO = status canonico derivado (STATUSCON preferido, LIBERADO fallback),
    // para o grid/filtro exibirem C/P/D/V/E/X de forma consistente com o resumo/cards.
    return this.query(
      `SELECT *, ${this.statusExpr()} AS STATUSEFETIVO
       FROM MKTPREGISTROSCONCILIADORA (NOLOCK)
       WHERE ${where} ORDER BY ${orderClause}
       OFFSET ${offset} ROWS FETCH NEXT ${limit} ROWS ONLY`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(this.mapSqlRows<ConciliadoraRegistro>());
    }));
  }

  /**
   * TODOS os registros pendentes (LIBERADO IS NULL) da conciliacao, SEM paginacao.
   * Usado pelo Processar para operar na conciliacao inteira (nao so a pagina
   * carregada no grid). Multi-tenant aplicado.
   */
  getRegistrosPendentes(codigo: number): Observable<ConciliadoraRegistro[]> {
    return this.ensureStatusCap().pipe(mergeMap(() => this.query(
      `SELECT * FROM MKTPREGISTROSCONCILIADORA (NOLOCK)
       WHERE CODIGO = ${codigo} AND ${this.companyFilter()}
         AND (${this.statusExpr()}) = 'P'
       ORDER BY SEQUENCIA ASC`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(this.mapSqlRows<ConciliadoraRegistro>())));
  }

  /**
   * Resumo por TIPO da conciliacao (aba/janela de resumo): contagens por status
   * + somas de valor liquido e provisao. Multi-tenant aplicado. Uma unica query
   * agregada (GROUP BY TIPO) — leve.
   */
  getResumoPorTipo(codigo: number): Observable<ConciliadoraResumoTipo[]> {
    return this.ensureStatusCap().pipe(mergeMap(() => this.query(
      // Classifica o status a partir do LIBERADO (texto livre) em 6 baldes:
      // P=Pendente, C=Conciliado, D=Divergente, E=Erro, X=Nao localizado, V=Provisao.
      // Quando a coluna STATUSCON (migracao 13) estiver populada pelo fluxo, este
      // CASE passa a preferir STATUSCON. Ordem das clausulas importa (divergente e
      // nao-localizado sao subtipos de "Erro:" na mensagem).
      `SELECT TIPO,
        COUNT(*) AS total,
        SUM(CASE WHEN st = 'P' THEN 1 ELSE 0 END) AS pendentes,
        SUM(CASE WHEN st = 'C' THEN 1 ELSE 0 END) AS liberados,
        SUM(CASE WHEN st = 'D' THEN 1 ELSE 0 END) AS divergentes,
        SUM(CASE WHEN st = 'E' THEN 1 ELSE 0 END) AS erros,
        SUM(CASE WHEN st = 'X' THEN 1 ELSE 0 END) AS naoLocalizados,
        SUM(CASE WHEN NUNOTA IS NULL OR LTRIM(RTRIM(NUNOTA)) = '' THEN 1 ELSE 0 END) AS semNota,
        SUM(ISNULL(VLRLIQ, 0)) AS vlrLiq,
        SUM(ISNULL(VLRPREV, 0)) AS vlrPrev,
        SUM(CASE WHEN st = 'P' THEN ISNULL(VLRLIQ, 0) ELSE 0 END) AS vlrPendente,
        SUM(CASE WHEN st = 'C' THEN ISNULL(VLRLIQ, 0) ELSE 0 END) AS vlrLiberado,
        SUM(CASE WHEN st = 'D' THEN ISNULL(VLRLIQ, 0) ELSE 0 END) AS vlrDivergente,
        SUM(CASE WHEN st = 'E' THEN ISNULL(VLRLIQ, 0) ELSE 0 END) AS vlrErro,
        SUM(CASE WHEN st = 'X' THEN ISNULL(VLRLIQ, 0) ELSE 0 END) AS vlrNaoLocalizado
       FROM (
         SELECT TIPO, NUNOTA, VLRLIQ, VLRPREV,
           ${this.statusExpr()} AS st
         FROM MKTPREGISTROSCONCILIADORA (NOLOCK)
         WHERE CODIGO = ${codigo} AND ${this.companyFilter()}
       ) q
       GROUP BY TIPO
       ORDER BY TIPO`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(
      map((res: any) => (res['SQL'] as any[] || []).map((r) => ({
        TIPO: r.TIPO || '(sem tipo)',
        total: Number(r.total) || 0,
        pendentes: Number(r.pendentes) || 0,
        liberados: Number(r.liberados) || 0,
        divergentes: Number(r.divergentes) || 0,
        erros: Number(r.erros) || 0,
        naoLocalizados: Number(r.naoLocalizados) || 0,
        semNota: Number(r.semNota) || 0,
        vlrLiq: Number(r.vlrLiq) || 0,
        vlrPrev: Number(r.vlrPrev) || 0,
        vlrPendente: Number(r.vlrPendente) || 0,
        vlrLiberado: Number(r.vlrLiberado) || 0,
        vlrDivergente: Number(r.vlrDivergente) || 0,
        vlrErro: Number(r.vlrErro) || 0,
        vlrNaoLocalizado: Number(r.vlrNaoLocalizado) || 0,
      })))
    )));
  }

  getRegistrosCount(codigo: number): Observable<DashboardStats> {
    return this.ensureStatusCap().pipe(mergeMap(() => this.query(
      `SELECT
        COUNT(*) AS total,
        SUM(CASE WHEN st = 'C' THEN 1 ELSE 0 END) AS liberados,
        SUM(CASE WHEN st = 'P' THEN 1 ELSE 0 END) AS pendentes,
        SUM(CASE WHEN st = 'D' THEN 1 ELSE 0 END) AS divergentes,
        SUM(CASE WHEN st = 'E' THEN 1 ELSE 0 END) AS erros,
        SUM(CASE WHEN st = 'X' THEN 1 ELSE 0 END) AS naoLocalizados,
        SUM(CASE WHEN st = 'V' THEN 1 ELSE 0 END) AS provisoes
       FROM (
         SELECT ${this.statusExpr()} AS st
         FROM MKTPREGISTROSCONCILIADORA (NOLOCK)
         WHERE CODIGO = ${codigo} AND ${this.companyFilter()}
       ) q`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(
      map((res: any) => {
        const r = (res['SQL'] as any[])?.[0];
        return {
          total: r?.total || 0,
          liberados: r?.liberados || 0,
          pendentes: r?.pendentes || 0,
          divergentes: r?.divergentes || 0,
          erros: r?.erros || 0,
          naoLocalizados: r?.naoLocalizados || 0,
          provisoes: r?.provisoes || 0,
        };
      })
    )));
  }

  /**
   * Contagens leves para o progresso REAL de Processar / Localizar Nota (polling
   * durante o job). O backend nao expoe progresso incremental do fluxo, entao a
   * tela mede o avanco pelo proprio estado dos registros:
   *  - Processar: registro sai de LIBERADO IS NULL.
   *  - Localizar Nota: registro ganha NUNOTA.
   */
  contarPendencias(codigo: number): Observable<{ total: number; pendentes: number; semNota: number; marco: string }> {
    return this.query(
      `SELECT COUNT(*) AS total,
        SUM(CASE WHEN LIBERADO IS NULL THEN 1 ELSE 0 END) AS pendentes,
        SUM(CASE WHEN NUNOTA IS NULL OR LTRIM(RTRIM(NUNOTA)) = '' THEN 1 ELSE 0 END) AS semNota,
        CONVERT(VARCHAR(23), GETDATE(), 121) AS marco
       FROM MKTPREGISTROSCONCILIADORA (NOLOCK)
       WHERE CODIGO = ${codigo} AND ${this.companyFilter()}`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(
      map((res: any) => {
        const r = (res['SQL'] as any[])?.[0] ?? {};
        return {
          total: Number(r.total) || 0,
          pendentes: Number(r.pendentes) || 0,
          semNota: Number(r.semNota) || 0,
          marco: String(r.marco || ''),
        };
      })
    );
  }

  /**
   * Quantos registros foram TOCADOS pelo fluxo desde o marco (relogio do servidor,
   * vindo de contarPendencias). Conta sucesso E falha (ex.: Localizar Nota grava
   * "Titulo nao Localizado" sem preencher NUNOTA — tratado mesmo assim). E o
   * numerador honesto do progresso.
   */
  contarTratados(codigo: number, marco: string): Observable<number> {
    return this.query(
      `SELECT COUNT(*) AS tratados
       FROM MKTPREGISTROSCONCILIADORA (NOLOCK)
       WHERE CODIGO = ${codigo} AND ${this.companyFilter()}
         AND MODIFIEDON >= '${this.sqlEscape(marco)}'`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(
      map((res: any) => Number((res['SQL'] as any[])?.[0]?.tratados) || 0)
    );
  }

  /**
   * Tipos distintos que REALMENTE existem nos registros do cabecalho — fonte
   * do dropdown de filtro "Tipo". Antes vinha de MKTPCONFTIPOSCONC.TIPOS (lista
   * agrupada por virgula, ex.: "SALE, REFUND"), que nao casa com o TIPO cru do
   * registro (ex.: INSTALLMENT_FEE / COMISSION) e deixava tipos fora do dropdown.
   */
  getTiposDistintos(codigo: number): Observable<string[]> {
    return this.query(
      `SELECT DISTINCT TIPO FROM MKTPREGISTROSCONCILIADORA (NOLOCK)
       WHERE CODIGO = ${codigo} AND ${this.companyFilter()} AND TIPO IS NOT NULL AND TIPO <> ''
       ORDER BY TIPO`,
      'MKTPREGISTROSCONCILIADORA'
    ).pipe(map((res: any) => ((res['SQL'] as any[]) || []).map((r) => r.TIPO).filter(Boolean)));
  }

  saveRegistro(registro: Partial<ConciliadoraRegistro>): Observable<any> {
    if (!registro.IDREGISTRO) return of(null);
    const ob: any = { IDREGISTRO: registro.IDREGISTRO };
    if (registro.VLRPREV !== undefined) ob.VLRPREV = registro.VLRPREV;
    if (registro.LIBERADO !== undefined) ob.LIBERADO = registro.LIBERADO;
    if (registro.STATUS !== undefined) ob.STATUS = registro.STATUS;
    if (registro.NUNOTA !== undefined) ob.NUNOTA = registro.NUNOTA;
    if (registro.NUFIN !== undefined) ob.NUFIN = registro.NUFIN;
    if (registro.NUFINNOVO !== undefined) ob.NUFINNOVO = registro.NUFINNOVO;
    ob.MODIFIEDON = new Date();
    return this.save('MKTPREGISTROSCONCILIADORA', ob);
  }

  // ========== ACOES DE PROCESSAMENTO ==========

  buscarRegistros(codigo: number, sqlBusca: string, nroConciliacao?: string | null): Observable<any> {
    return this.query(this.resolveBuscaParams(sqlBusca, nroConciliacao), 'MKTPREGISTROSCONCILIADORA');
  }

  /**
   * Resolve as variaveis do SQLBUSCA antes de mandar pro findnative (que executa
   * o SQL cru, sem substituir nada). Hoje resolve @NRO_CONCILIACAO -> literal de
   * texto escapado (anti-injection). O valor vem do campo Nro. Conciliacao do
   * cabecalho; permite filtrar a busca em vez de varrer a base inteira.
   */
  private resolveBuscaParams(sql: string, nro?: string | null): string {
    if (!sql) return sql;
    const val = this.sqlEscape((nro ?? '').toString());
    return sql.replace(/@NRO_CONCILIACAO\b/gi, `'${val}'`);
  }

  executarConfiguracao(
    idConf: number,
    registroData: Record<string, any>,
    opts?: ExecutarOpts
  ): Observable<any> {
    if (opts?.newMode) {
      return this.executarFlowOnline(idConf, [registroData], opts);
    }
    const action = {
      NAME: 'execute',
      TYPE: 'crud',
      IDCONF: idConf,
    };
    return this.http.executeBackEndAction(
      'Mktpcfg',
      action as any,
      [registroData as any],
      null
    );
  }

  executarConfiguracaoLote(
    idConf: number,
    registros: Record<string, any>[],
    opts?: ExecutarOpts
  ): Observable<any> {
    if (opts?.newMode) {
      return this.executarFlowOnline(idConf, registros, opts);
    }
    const action = {
      NAME: 'execute',
      TYPE: 'crud',
      IDCONF: idConf,
    };
    return this.http.executeBackEndAction(
      'Mktpcfg',
      action as any,
      registros as any,
      null
    );
  }

  /**
   * Feature toggle para fluxos NEWMODE='S'.
   * Quando true, executarFlowOnline chama POST /rest/FlowServices/runOnline.
   * Pode ser desligado em ambientes sem o backend novo publicado.
   */
  static NEWMODE_FEATURE_ENABLED = true;

  /** Tamanho maximo de chunk por chamada ao endpoint /FlowServices/runOnline. */
  static readonly NEWMODE_CHUNK_SIZE = 200;

  /**
   * Executa um IDCONF NEWMODE='S' atraves do endpoint sincrono /FlowServices/runOnline.
   *
   * Convencao das variaveis injetadas no globalMap do FlowRuntime:
   *  - __rows: Array com as linhas (objetos com colunas de MKTPREGISTROSCONCILIADORA).
   *  - __metadados: object com PARAMETROS (DPARA) do MKTPCONFTIPOSCONC.
   *  - __contextoAcao: 'PROCESSAR' | 'LOCALIZAR_NOTA' | 'REPROCESSAR'.
   *  - __nuconf, __codigoCab, __userName: rastreio.
   *
   * Quando rows.length > NEWMODE_CHUNK_SIZE divide em chunks e executa sequencialmente
   * (concat) para nao sobrecarregar o backend. O resultado eh consolidado num unico
   * objeto { ok, newMode: true, rowsCount, results: [...] }.
   */
  executarFlowOnline(
    idConf: number,
    rows: Record<string, any>[],
    opts: ExecutarOpts
  ): Observable<any> {
    if (!ConciliadoraService.NEWMODE_FEATURE_ENABLED) {
      return throwError(
        () =>
          new Error(
            'Fluxo NEWMODE ainda nao habilitado nesta instancia. ' +
              'Aguarde o endpoint /rest/FlowServices/runOnline ser publicado no backend.'
          )
      );
    }

    const codecompany =
      opts.codecompany ?? this.selectedCompanyCode ?? Number(this.codecompany) ?? 0;

    const total = rows?.length ?? 0;
    const chunkSize = ConciliadoraService.NEWMODE_CHUNK_SIZE;
    const chunks: Record<string, any>[][] = [];
    if (total === 0) {
      chunks.push([]);
    } else {
      for (let i = 0; i < total; i += chunkSize) {
        chunks.push(rows.slice(i, i + chunkSize));
      }
    }

    const callOne = (chunk: Record<string, any>[]): Observable<any> => {
      const payload = {
        idconf: idConf,
        codecompany,
        codemktp: opts.codemktp ?? null,
        sequence: opts.sequence ?? 0,
        context: {
          __contextoAcao: opts.contextoAcao,
          __nuconf: opts.nuconf ?? null,
          __codigoCab: opts.codigoCab ?? null,
          __metadados: opts.metadados ?? null,
          __userName: this.uas.user?.username ?? null,
          __logEnabled: opts.logEnabled ?? true,
        },
        rows: chunk,
      };

      return this.http
        .post(payload, 'FlowServices/runOnline', false, true, true, false)
        .pipe(
          map((res: any) => {
            if (res && res.statusRequest === '2') {
              throw new Error(res.message || 'Falha na execucao do fluxo NEWMODE.');
            }
            if (res && res.ok === false) {
              throw new Error(res.message || 'Falha na execucao do fluxo NEWMODE.');
            }
            return res;
          })
        );
    };

    if (chunks.length === 1) {
      return callOne(chunks[0]);
    }

    // Multiplos chunks: executa sequencialmente (concorrencia=1) e consolida via reduce.
    return from(chunks).pipe(
      mergeMap((c) => callOne(c), 1),
      reduce(
        (acc: { ok: boolean; newMode: boolean; rowsCount: number; results: any[] }, res: any) => ({
          ok: acc.ok,
          newMode: acc.newMode,
          rowsCount: acc.rowsCount + Number(res?.rowsCount ?? 0),
          results: [...acc.results, res],
        }),
        { ok: true, newMode: true, rowsCount: 0, results: [] as any[] }
      )
    );
  }

  // ========== FLUXO NEWMODE ASSINCRONO (Fase B) ==========

  /**
   * Dispara um fluxo NEWMODE='S' em modo assincrono no backend. Retorna
   * imediatamente um {@link FlowJobStart} contendo o `idjob` que deve ser
   * pollado via {@link consultarJobStatus}.
   *
   * Nao faz chunking: envia TODAS as `rows` em uma unica chamada, deixando o
   * `ForEach` interno do fluxo gerenciar a iteracao.
   */
  iniciarFlowAsync(
    idConf: number,
    rows: Record<string, any>[],
    opts: ExecutarOpts
  ): Observable<FlowJobStart> {
    if (!ConciliadoraService.NEWMODE_FEATURE_ENABLED) {
      return throwError(
        () =>
          new Error(
            'Fluxo NEWMODE ainda nao habilitado nesta instancia. ' +
              'Aguarde o endpoint /rest/FlowServices/runOnlineAsync ser publicado no backend.'
          )
      );
    }

    const codecompany =
      opts.codecompany ?? this.selectedCompanyCode ?? Number(this.codecompany) ?? 0;

    const payload = {
      idconf: idConf,
      codecompany,
      codemktp: opts.codemktp ?? null,
      sequence: opts.sequence ?? 0,
      context: {
        __contextoAcao: opts.contextoAcao,
        __nuconf: opts.nuconf ?? null,
        __codigoCab: opts.codigoCab ?? null,
        __metadados: opts.metadados ?? null,
        __userName: this.uas.user?.username ?? null,
        __logEnabled: opts.logEnabled ?? true,
      },
      rows: rows ?? [],
    };

    return this.http
      .post(payload, 'FlowServices/runOnlineAsync', false, true, true, false)
      .pipe(
        map((res: any): FlowJobStart => {
          if (res && res.statusRequest === '2') {
            throw new Error(res.message || 'Falha ao disparar fluxo assincrono.');
          }
          if (res && res.ok === false) {
            throw new Error(res.message || 'Falha ao disparar fluxo assincrono.');
          }
          return res as FlowJobStart;
        })
      );
  }

  /**
   * Dispara a importacao server-side dos registros da conciliacao (INSERT ... SELECT
   * set-based, dedup por ID_KONCILI, tenant do cabecalho). Assincrono: retorna o idjob
   * na hora e o caller acompanha via {@link consultarJobStatus}. Substitui a busca antiga
   * (findnative + save por registro) que estourava o rate-limit e era lenta linha-a-linha.
   */
  importarRegistros(codigo: number): Observable<FlowJobStart> {
    return this.http
      .post({ codigo }, 'ConciliadoraServices/importarRegistros', false, true, true, false)
      .pipe(
        map((res: any): FlowJobStart => {
          if (res && res.statusRequest === '2') {
            throw new Error(res.message || 'Falha ao importar registros.');
          }
          if (res && res.ok === false) {
            throw new Error(res.error || res.message || 'Falha ao importar registros.');
          }
          return res as FlowJobStart;
        })
      );
  }

  /** Consulta o estado de um job iniciado via {@link iniciarFlowAsync}. */
  consultarJobStatus(idjob: string): Observable<FlowJobStatus> {
    return this.http
      .post({ idjob }, 'FlowServices/jobStatus', false, true, true, false)
      .pipe(
        map((res: any): FlowJobStatus => {
          if (res && res.statusRequest === '2') {
            throw new Error(res.message || 'Falha ao consultar status do job.');
          }
          return res as FlowJobStatus;
        })
      );
  }

  /** Solicita interrupcao da thread que executa o fluxo. */
  cancelarJob(idjob: string): Observable<{ ok: boolean; message?: string }> {
    return this.http
      .post({ idjob }, 'FlowServices/jobCancel', false, true, true, false)
      .pipe(
        map((res: any) => {
          if (res && res.statusRequest === '2') {
            throw new Error(res.message || 'Falha ao cancelar job.');
          }
          return res;
        })
      );
  }

  // ========== ACTION LEGACY ASYNC (Fase B1 - Q2) ==========

  /**
   * Dispara um IDCONF Legacy (NEWMODE != 'S') em modo assincrono.
   * Backend: POST /rest/FlowServices/runActionAsync (criado na Fase B1).
   * Reaproveita os endpoints existentes `jobStatus` / `jobCancel`.
   *
   * Quando o endpoint nao estiver disponivel (404), o caller (FlowExecutorService)
   * deve cair em LEGACY_SYNC usando `executeBackEndAction` por linha.
   */
  iniciarActionAsync(
    idConf: number,
    rows: Record<string, any>[],
    opts: ExecutarOpts
  ): Observable<FlowJobStart> {
    const codecompany =
      opts.codecompany ?? this.selectedCompanyCode ?? Number(this.codecompany) ?? 0;

    const payload = {
      idconf: idConf,
      codecompany,
      codemktp: opts.codemktp ?? null,
      sequence: opts.sequence ?? 0,
      context: {
        __contextoAcao: opts.contextoAcao,
        __nuconf: opts.nuconf ?? null,
        __codigoCab: opts.codigoCab ?? null,
        __metadados: opts.metadados ?? null,
        __userName: this.uas.user?.username ?? null,
        __logEnabled: opts.logEnabled ?? true,
      },
      rows: rows ?? [],
    };

    return this.http
      .post(payload, 'FlowServices/runActionAsync', false, true, true, false)
      .pipe(
        map((res: any): FlowJobStart => {
          if (res && res.statusRequest === '2') {
            throw new Error(res.message || res.MESSAGE || 'Falha ao disparar acao assincrona.');
          }
          if (res && res.ok === false) {
            throw new Error(res.message || res.MESSAGE || 'Falha ao disparar acao assincrona.');
          }
          return res as FlowJobStart;
        })
      );
  }

  // ========== HISTORICO DE EXECUCOES - MKTPFLOWEXECLOG (Fase B0 - Q3) ==========

  /**
   * Cria registro em MKTPFLOWEXECLOG.
   * Endpoint best-effort: se backend nao estiver disponivel, caller deve seguir
   * adiante sem persistencia (somente in-memory + localStorage).
   */
  flowExecLogStart(req: FlowExecLogStartRequest): Observable<FlowExecLogStartResponse> {
    const enriched: FlowExecLogStartRequest = {
      ...req,
      codecompany: req.codecompany || Number(this.codecompany) || 0,
      codecontract: req.codecontract || Number(this.codecontract) || null,
      username: req.username || this.username || 'anonymous',
      screenorigin: req.screenorigin || 'CONCILIADORA',
    };
    return this.http
      .post(enriched, 'FlowExecLogServices/start', false, true, true, false)
      .pipe(
        map((res: any): FlowExecLogStartResponse => {
          if (res && (res.statusRequest === '2' || res.ok === false)) {
            throw new Error(res.message || res.MESSAGE || 'Falha ao registrar inicio de execucao.');
          }
          return res as FlowExecLogStartResponse;
        })
      );
  }

  /** Atualiza registro de execucao com status final. */
  flowExecLogFinish(req: FlowExecLogFinishRequest): Observable<{ ok: boolean }> {
    return this.http
      .post(req, 'FlowExecLogServices/finish', false, true, true, false)
      .pipe(
        map((res: any) => {
          if (res && (res.statusRequest === '2' || res.ok === false)) {
            throw new Error(res.message || res.MESSAGE || 'Falha ao finalizar registro.');
          }
          return res as { ok: boolean };
        })
      );
  }

  /** Lista paginada de execucoes (apenas registros pai). */
  flowExecLogList(filter: FlowExecLogListFilter): Observable<FlowExecLogListResponse> {
    const enriched: FlowExecLogListFilter = {
      ...filter,
      codecompany: filter.codecompany ?? Number(this.codecompany) ?? 0,
      offset: filter.offset ?? 0,
      limit: filter.limit ?? 20,
    };
    return this.http
      .post(enriched, 'FlowExecLogServices/list', false, true, true, true)
      .pipe(
        map((res: any): FlowExecLogListResponse => {
          if (res && (res.statusRequest === '2' || res.ok === false)) {
            throw new Error(res.message || res.MESSAGE || 'Falha ao listar historico.');
          }
          return res as FlowExecLogListResponse;
        })
      );
  }

  /** Detalhes de uma execucao (pai + filhos + payloads completos). */
  flowExecLogGet(idexeclog: number): Observable<FlowExecLogGetResponse> {
    const payload = {
      idexeclog,
      codecompany: Number(this.codecompany) ?? 0,
    };
    return this.http
      .post(payload, 'FlowExecLogServices/get', false, true, true, true)
      .pipe(
        map((res: any): FlowExecLogGetResponse => {
          if (res && (res.statusRequest === '2' || res.ok === false)) {
            throw new Error(res.message || res.MESSAGE || 'Falha ao obter detalhes.');
          }
          return res as FlowExecLogGetResponse;
        })
      );
  }

  // ========== CONFIGURACOES MKTPCFG DISPONIVEIS ==========

  /**
   * Lista fluxos (MKTPCFG) disponiveis para selecao nos selects de Configuracao
   * (geral e por tipo).
   *
   * Filtros:
   * - ADMIN: ve tudo (0=0)
   * - Demais usuarios: fluxos com CODECOMPANY direto da empresa OU fluxos
   *   compartilhados (CODECOMPANY=0) com vinculo em MKTPCFGCOMPANY para a
   *   empresa do usuario - mesmo padrao usado em `flow-config-modal`,
   *   `inspector` e `flow-action`.
   *
   * Observacoes:
   * - NAO filtra por `ONLINE='S'` (intencional). Fluxos NEWMODE podem ter ONLINE
   *   em branco ou 'N' e ainda assim ser executaveis via /runOnline ou
   *   /runOnlineAsync. O backend valida.
   * - NAO filtra por `NEWMODE`. Lista todos; o frontend roteia (legado vs
   *   NEWMODE) e exibe badge "Novo" nos selects.
   */
  getConfigsMktpCfg(): Observable<MktpCfgOption[]> {
    const isAdmin = this.uas.user?.profile === 'ADMIN';
    const companies = this.uas.getCompanies();
    const companyFilter = isAdmin
      ? '0=0'
      : `(
          A.CODECOMPANY IN (${companies})
          OR (
            A.CODECOMPANY = 0
            AND EXISTS (
              SELECT 1 FROM MKTPCFGCOMPANY X (NOLOCK)
              WHERE X.IDCONF = A.IDCONF AND X.CODECOMPANY IN (${companies})
            )
          )
        )`;

    return this.query(
      `SELECT A.IDCONF, A.NAME, A.NEWMODE
       FROM MKTPCFG A (NOLOCK)
       WHERE ${companyFilter}
       ORDER BY A.NAME`,
      'Mktpcfg'
    ).pipe(
      this.mapSqlRows<any>(),
      map((rows: any[]) => {
        // dedup por IDCONF: a tela exibia fluxos repetidos quando a fonte
        // retornava o mesmo IDCONF mais de uma vez. Garante 1 opcao por codigo.
        const seen = new Set<number>();
        const out: MktpCfgOption[] = [];
        for (const r of rows || []) {
          const id = Number(r.IDCONF);
          if (seen.has(id)) continue;
          seen.add(id);
          out.push({
            IDCONF: id,
            NAME: String(r.NAME ?? ''),
            NEWMODE: r.NEWMODE ?? null,
            newMode: String(r.NEWMODE ?? '').toUpperCase() === 'S',
          });
        }
        return out;
      })
    );
  }

  // ========== INSERIR REGISTROS EM LOTE ==========

  inserirRegistrosEmLote(
    codigo: number,
    codecompany: number,
    codecontract: number | null,
    registros: any[],
    mapeamentoJson?: string | null,
    concurrency = 8
  ): Observable<BatchProgress> {
    const total = registros?.length ?? 0;
    if (!total) {
      return of<BatchProgress>({ ok: 0, err: 0, total: 0, done: true });
    }

    let mapeamento: MapeamentoColuna[] = [];
    if (mapeamentoJson) {
      try { mapeamento = JSON.parse(mapeamentoJson); } catch { /* ignore */ }
    }

    const mapValue = (row: any, destino: string): any => {
      const m = mapeamento.find((x) => x.destino === destino);
      const key = m ? m.origem : destino;
      return row[key] ?? null;
    };

    const saves: Observable<{ success: boolean }>[] = registros.map((r, i) => {
      const ob: any = {
        CODIGO: codigo,
        CODECOMPANY: codecompany,
        CODECONTRACT: codecontract,
        CREATEDON: new Date(),
      };
      for (const col of COLUNAS_REGISTRO) {
        if (col === 'LIBERADO') continue;
        ob[col] = mapValue(r, col);
      }
      if (ob.SEQUENCIA == null) ob.SEQUENCIA = i + 1;
      return this.save('MKTPREGISTROSCONCILIADORA', ob).pipe(
        map(() => ({ success: true })),
        catchError(() => of({ success: false }))
      );
    });

    return new Observable<BatchProgress>((subscriber) => {
      let ok = 0;
      let err = 0;
      const sub = from(saves)
        .pipe(mergeMap((obs) => obs, concurrency))
        .subscribe({
          next: (r) => {
            if (r.success) ok++; else err++;
            subscriber.next({ ok, err, total, done: false });
          },
          error: (e) => subscriber.error(e),
          complete: () => {
            subscriber.next({ ok, err, total, done: true });
            subscriber.complete();
          },
        });
      return () => sub.unsubscribe();
    });
  }
}
